import { Box, Button, CircularProgress, Modal, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import ModalV2 from '/@/components/bayer/modal-v2';
import Spinner from '/@/components/spinner';
import { useGetUserNode, useOwnUserNode, useSendFieldviewInvitation } from '/@/hooks/nodes';
import { useCreateOrUpdateCropMonitoringResult } from '/@/hooks/reports';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { linkFieldviewAccount } from '/@/store/session/actions';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';
import FieldviewReportsErrorModal from './components/fieldview-reports-error';
import CreateReportForm from './components/form';
import useFieldviewReports from './hooks/use-fieldview-reports';
import { useReportForm } from './hooks/use-report-form';

const CreateReportModal = ({
    field,
    cropMonitoring,
    isOpen,
    setIsOpen,
}: {
    field: Field;
    cropMonitoring: CropMonitoring;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fieldIsFieldview = field.externalProvider === 'fieldview';
    const isBayerUserType = useIsValidatedBayerUserType();
    const ownUserQuery = useOwnUserNode();
    const isOwnField = field.userNode === ownUserQuery.data?.id;
    const fieldOwnerNode = useGetUserNode(field.userNode);
    const sendFieldviewInvitation = useSendFieldviewInvitation();
    const showRequestAuth = !isOwnField && isBayerUserType;

    const [manualMode, setManualMode] = useState(false);
    const [showInvitationScreen, setShowInvitationScreen] = useState(false);

    const fieldviewReports = useFieldviewReports({ field });
    const { createReports } = fieldviewReports;
    // Only fetch/generate FieldView reports once the modal is actually opened —
    // the modal can be mounted (e.g. on the detail page) while closed, and
    // create-all triggers backend sync/auto-harvest, so it must not run on mount.
    useEffect(() => {
        if (isOpen && fieldIsFieldview) createReports();
    }, [isOpen, fieldIsFieldview, createReports]);

    const reportForm = useReportForm({
        field,
        cropMonitoring,
        isFieldviewType: fieldIsFieldview,
        enabled: isOpen && fieldIsFieldview,
        reportsMutating: fieldviewReports.isMutating,
        reportsError: fieldviewReports.error,
    });
    const { form } = reportForm;

    const createOrUpdateCropMonitoringResult = useCreateOrUpdateCropMonitoringResult({
        cropMonitoringId: cropMonitoring?.id,
        fieldId: field.id,
    });

    const hasError = fieldIsFieldview && Boolean(fieldviewReports.error);
    const hasNoData = fieldIsFieldview && reportForm.harvestDataResolved && !reportForm.hasHarvestData;
    // While FieldView reports are still being fetched/generated we show the
    // loading screen instead of the (empty) form, so the modal never flashes
    // form → error.
    const fetchPending = isOpen && fieldIsFieldview && reportForm.reportsPending;
    const showErrorModal = isOpen && !manualMode && (hasError || hasNoData);
    const showForm = isOpen && !showErrorModal;

    const title = (() => {
        if (fieldIsFieldview && reportForm.bothAutoSelected) return _('Datos de cosecha encontrados en FieldView');
        return _('Completá los datos de la cosecha');
    })();

    const handleResync = () => {
        dispatch(
            linkFieldviewAccount({
                source: 'field-reports',
                onSuccess: () => {
                    setManualMode(false);
                    fieldviewReports.createReports();
                },
            })
        );
    };

    const handleRequestAuth = async () => {
        const invitee = fieldOwnerNode.data;
        if (!invitee?.email) return;
        await sendFieldviewInvitation.trigger({
            invitedUserName: invitee.name || invitee.email,
            invitedUserEmail: invitee.email,
            sharedNodeId: invitee.id,
        });
        setShowInvitationScreen(true);
    };

    const submitDisabled = createOrUpdateCropMonitoringResult.isMutating;

    return (
        <>
            <FieldviewReportsErrorModal
                open={showErrorModal}
                variant={hasError ? 'error' : 'no-data'}
                isOwnField={isOwnField}
                showRequestAuth={showRequestAuth}
                showInvitationScreen={showInvitationScreen}
                userNodeId={field.userNode}
                requestAuthMutating={sendFieldviewInvitation.isMutating}
                onClose={() => setIsOpen(false)}
                onLoadManually={() => setManualMode(true)}
                onResync={handleResync}
                onRequestAuth={handleRequestAuth}
                onInvitationAck={() => setShowInvitationScreen(false)}
            />

            <Modal
                slotProps={{ backdrop: { sx: { backgroundColor: 'rgb(255 255 255)' } } }}
                open={createOrUpdateCropMonitoringResult.isMutating}
            >
                <Stack spacing={4} alignItems='center' justifyContent='center' height='100%'>
                    <Typography color='primary' variant='h5'>
                        {_('Generando reporte...')}
                    </Typography>
                    <Box sx={{ color: 'primary.onSurface' }}>
                        <CircularProgress color='inherit' size={75} />
                    </Box>
                    <Typography color='primary.mui.inactive' variant='body2'>
                        {_('Vas a poder ver cómo fue tu campaña y definir los próximos pasos con más datos.')}
                    </Typography>
                </Stack>
            </Modal>

            <ModalV2
                open={showForm}
                onClose={() => setIsOpen(false)}
                title={title}
                actions={
                    <>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => {
                                setIsOpen(false);
                                form.reset();
                            }}
                            disabled={createOrUpdateCropMonitoringResult.isMutating}
                        >
                            {_('Cancelar')}
                        </Button>
                        <Button
                            type='submit'
                            variant='contained'
                            color='secondary'
                            disabled={submitDisabled}
                            startIcon={submitDisabled ? <CircularProgress size={20} /> : undefined}
                        >
                            {_('Generar reporte')}
                        </Button>
                    </>
                }
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth='sm'
                containerProps={{
                    component: 'form',
                    onSubmit: form.handleSubmit(async (data) => {
                        try {
                            await createOrUpdateCropMonitoringResult.trigger({
                                plantingReport: data.plantingRequestId || undefined,
                                harvestReport: data.harvestRequestId || undefined,
                                plantingDate: data.plantingDate?.format('YYYY-MM-DD'),
                                harvestDate: data.harvestDate?.format('YYYY-MM-DD'),
                                averageYield: data.yield ?? undefined,
                                humidityAtHarvest: data.humidity ? Number(data.humidity) : undefined,
                            });
                        } catch {
                            return;
                        }
                        setIsOpen(false);
                        navigate(
                            reverse('cropMonitoring.CornCropMonitoringDetail.report', {
                                params: { cropMonitoringId: cropMonitoring.id },
                            })
                        );
                    }),
                }}
            >
                {fetchPending ? (
                    <Spinner />
                ) : (
                    <CreateReportForm
                        cropMonitoring={cropMonitoring}
                        field={field}
                        form={form}
                        units={reportForm.units}
                        plantingDateOptions={reportForm.plantingDateOptions}
                        harvestDateOptions={reportForm.harvestDateOptions}
                        plantingDateBounds={reportForm.plantingDateBounds}
                        plantingDisabled={reportForm.plantingDisabled}
                        bothAutoSelected={reportForm.bothAutoSelected}
                    />
                )}
            </ModalV2>
        </>
    );
};

export default CreateReportModal;
